<template>
  <div class="modern-master-view pb-12">
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm">
          <v-icon color="white">fas fa-phone-volume</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Cobranza</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">
            Gestiones sobre cartera Simple Factura
          </div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn color="success" class="font-weight-black text-uppercase rounded-lg px-6" @click="openCreate">
        <v-icon left small>fas fa-plus</v-icon> Nueva gestión
      </v-btn>
    </v-row>

    <v-row class="mb-6 px-4">
      <v-col cols="12" md="6">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Cartera vencida</div>
          <div class="text-h5 font-weight-black error--text">{{ summary.overdue_balance | moneda }}</div>
          <div class="caption grey--text">{{ summary.overdue_count || 0 }} documentos</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Saldo abierto</div>
          <div class="text-h5 font-weight-black primary--text">{{ summary.open_balance | moneda }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-card outlined class="modern-card mx-4">
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :items-per-page="50"
        hide-default-footer
        class="elevation-0"
        no-data-text="Aún no hay gestiones de cobranza."
      >
        <template v-slot:item.action_date="{ item }">{{ formatDateTime(item.action_date) }}</template>
        <template v-slot:item.promise_date="{ item }">{{ formatDate(item.promise_date) }}</template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="520">
      <v-card>
        <v-card-title class="font-weight-black">Nueva gestión</v-card-title>
        <v-card-text>
          <v-select
            v-model="form.ar_document_id"
            :items="openDocs"
            item-text="label"
            item-value="id"
            label="Documento"
            outlined dense
            @change="onDocChange"
          />
          <v-select v-model="form.action_type" :items="actionItems" item-text="text" item-value="value" label="Tipo" outlined dense />
          <v-text-field v-model="form.promise_date" type="date" label="Compromiso de pago" outlined dense clearable />
          <v-textarea v-model="form.notes" label="Notas" outlined dense rows="3" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="submit">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import notifications from '@/utils/notifications'
import {
  createCollectionAction,
  fetchArDocuments,
  fetchArSummary,
  fetchCollectionActions,
} from '@/api/finance'

export default {
  name: 'FinanceCollections',
  data() {
    return {
      loading: false,
      saving: false,
      dialog: false,
      items: [],
      summary: {},
      openDocs: [],
      form: { ar_document_id: null, client_id: null, action_type: 'call', promise_date: '', notes: '' },
      headers: [
        { text: 'Fecha', value: 'action_date' },
        { text: 'Tipo', value: 'action_type_label' },
        { text: 'Cliente', value: 'receiver_name' },
        { text: 'Folio', value: 'folio' },
        { text: 'Compromiso', value: 'promise_date' },
        { text: 'Notas', value: 'notes' },
      ],
    }
  },
  computed: {
    actionItems() {
      const labels = this.summary.collection_action_types || {
        call: 'Llamada',
        email: 'Correo',
        whatsapp: 'WhatsApp',
        payment_promise: 'Compromiso de pago',
        note: 'Observación',
      }
      return Object.keys(labels).map(value => ({ value, text: labels[value] }))
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return '—'
      const [y, m, d] = String(value).slice(0, 10).split('-')
      return `${d}/${m}/${y}`
    },
    formatDateTime(value) {
      if (!value) return '—'
      return this.formatDate(value)
    },
    async fetchAll() {
      this.loading = true
      try {
        const [summary, actions] = await Promise.all([
          fetchArSummary(),
          fetchCollectionActions({ page_size: 100 }),
        ])
        this.summary = summary
        this.items = actions.items || []
      } catch (e) {
        notifications.showError('No se pudo cargar cobranza')
      } finally {
        this.loading = false
      }
    },
    async openCreate() {
      try {
        const data = await fetchArDocuments({ open_only: true, page_size: 100 })
        this.openDocs = (data.items || []).map(row => ({
          ...row,
          label: `${row.folio || 's/n'} · ${row.receiver_name || ''} · saldo ${row.balance}`,
        }))
      } catch (e) {
        this.openDocs = []
      }
      this.form = { ar_document_id: null, client_id: null, action_type: 'call', promise_date: '', notes: '' }
      this.dialog = true
    },
    onDocChange(id) {
      const doc = this.openDocs.find(row => row.id === id)
      this.form.client_id = doc ? doc.client_id : null
    },
    async submit() {
      if (!this.form.ar_document_id || !this.form.client_id) {
        notifications.showError('Seleccione un documento')
        return
      }
      this.saving = true
      try {
        await createCollectionAction({
          client_id: this.form.client_id,
          ar_document_id: this.form.ar_document_id,
          action_type: this.form.action_type,
          promise_date: this.form.promise_date || null,
          notes: this.form.notes,
        })
        notifications.showSuccess('Gestión registrada')
        this.dialog = false
        await this.fetchAll()
      } catch (e) {
        notifications.showError('No se pudo guardar la gestión')
      } finally {
        this.saving = false
      }
    },
  },
  created() {
    this.fetchAll()
  },
}
</script>
